import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link as LinkIcon, Package, Coins, Clock, Copy, CheckCircle, Hourglass } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MyOrdersPage = () => {
  const [extOrders, setExtOrders] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [section, setSection] = useState('external');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [e, b] = await Promise.all([
          axios.get(`${API}/api/store/external-subs/orders/mine`, { withCredentials: true }),
          axios.get(`${API}/api/store/bundles/purchases/mine`, { withCredentials: true }),
        ]);
        setExtOrders(e.data);
        setBundles(b.data);
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, []);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Link copied');
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="my-orders-page">
      <h1 className="font-heading text-3xl font-bold text-white mb-6">My Purchases</h1>

      <div className="flex gap-1 bg-slate-900/50 rounded-xl p-1 w-fit mb-6">
        <button onClick={() => setSection('external')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 ${section === 'external' ? 'bg-blue-500/20 text-blue-400' : 'text-slate-500 hover:text-white'}`}
          data-testid="orders-tab-external">
          <LinkIcon className="w-4 h-4" /> External Access ({extOrders.length})
        </button>
        <button onClick={() => setSection('bundles')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 ${section === 'bundles' ? 'bg-amber-500/20 text-amber-400' : 'text-slate-500 hover:text-white'}`}
          data-testid="orders-tab-bundles">
          <Package className="w-4 h-4" /> Bundles ({bundles.length})
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : section === 'external' ? (
        extOrders.length === 0 ? (
          <div className="glass-light rounded-2xl p-12 text-center">
            <LinkIcon className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">You haven't purchased any external access yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {extOrders.map(o => (
              <div key={o.id} className="glass-light rounded-2xl p-4" data-testid={`order-card-${o.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">{o.platform}</span>
                    <p className="text-white font-medium mt-1">{o.title}</p>
                    <p className="text-xs text-slate-500">
                      Expires {new Date(o.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  {o.delivered ? (
                    <div className="flex items-center gap-2">
                      <a href={o.access_link} target="_blank" rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600 flex items-center gap-1"
                        data-testid={`open-link-${o.id}`}>
                        <CheckCircle weight="fill" className="w-4 h-4" /> Open
                      </a>
                      <button onClick={() => copy(o.access_link)}
                        className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-white"
                        data-testid={`copy-link-${o.id}`}>
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <span className="flex items-center gap-1 text-amber-400 text-sm">
                      <Hourglass className="w-4 h-4" /> Awaiting creator delivery
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        bundles.length === 0 ? (
          <div className="glass-light rounded-2xl p-12 text-center">
            <Package className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">You haven't unlocked any bundles yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bundles.map(p => (
              <div key={p.purchase_id} className="glass-light rounded-2xl p-4" data-testid={`owned-bundle-${p.bundle.id}`}>
                <h4 className="font-semibold text-white">{p.bundle.title}</h4>
                <p className="text-xs text-slate-500 capitalize mt-1">{p.bundle.bundle_type} · {p.bundle.item_count} items</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5 text-sm">
                  <span className="flex items-center gap-1 text-slate-400">
                    <Coins weight="fill" className="w-4 h-4 text-amber-400" /> {p.bundle.price_tokens}
                  </span>
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {new Date(p.purchased_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default MyOrdersPage;
